import { Suspense } from "react";
import { getBlokComponent } from "../../defineBloks";
import type {
  BaseSBType,
  BlokWithFallback,
  BlokWithFetcher,
} from "../../type-generators";
import type { StoryblokComponentProps } from "./types";

export const ServerComponent = ({ blok, ...rest }: StoryblokComponentProps) => {
  const Component = getBlokComponent(blok.component);

  if (!Component) {
    const errorMessage = `Component: "${blok.component}" not found`;
    return <div>{errorMessage}</div>;
  }

  if (Component && typeof Component === "object") {
    if ("fetcher" in Component) {
      return (
        <Suspense
          fallback={
            Component.fallback ? (
              <Component.fallback {...rest} blok={blok} />
            ) : null
          }
        >
          <ServerComponentWithFetcher
            {...rest}
            blok={blok}
            componentData={Component}
          />
        </Suspense>
      );
    }

    return (
      <ServerComponentOnly {...rest} blok={blok} componentData={Component} />
    );
  }

  return <Component {...rest} blok={blok} />;
};

const ServerComponentOnly = ({
  blok,
  componentData,
  ...rest
}: StoryblokComponentProps & {
  componentData: BlokWithFallback<BaseSBType, {}>;
}) => {
  if (!componentData) {
    const errorMessage = `Component: "${blok.component}" not found`;
    return <div>{errorMessage}</div>;
  }

  const Blok = componentData.blok;

  return <Blok {...rest} blok={blok} />;
};

const ServerComponentWithFetcher = async ({
  blok,
  componentData,
  ...rest
}: StoryblokComponentProps & {
  componentData: BlokWithFetcher<BaseSBType, {}>;
}) => {
  if (!componentData) {
    const errorMessage = `Component: "${blok.component}" not found`;
    return <div>{errorMessage}</div>;
  }

  const data = await componentData.fetcher({ blok, preview: rest.preview });

  const Blok = componentData.blok;

  return <Blok {...rest} blok={blok} data={data as any} />;
};
